import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import useEntitlement from "../hooks/useEntitlement";
import { PaidAssessmentAllowancePanel } from "../components/PaidAssessmentAllowance";
import { getCachedFxRates, convertMoney, guessCurrencySymbol } from "../lib/fxRates";

import {
  getAuditSnapshotPayload,
  readAuditSnapshotIndex,
  rebuildAuditSnapshotIndexFromPayloads,
  getActiveBusiness,
  getLatestSnapshotForBusiness,
} from "../lib/auditHistory";

const STORAGE_KEYS = {
  USER: "aifine_user",
  ACTIVE_BUSINESS_ID: "aifine_active_business_id_v1",
  BUSINESS_INDEX: "aifine_business_index_v1",
  BUSINESS_PAYLOAD_PREFIX: "aifine_business_payload_",
  CONFIG: "aifine_config",
  PROGRESS_PREFIX: "aifine_assessment_progress_business_",
  ANSWERS_PREFIX: "aifine_answers_business_",
};

const STANDARD_SINGLE_ID = "single_default";

function safeParse(raw, fallback = null) {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function readLocalUser() {
  try {
    return safeParse(localStorage.getItem(STORAGE_KEYS.USER), null);
  } catch {
    return null;
  }
}

function readSubscriptionTier() {
  const obj = readLocalUser();
  const tier = obj?.subscription_tier ?? obj?.subscriptionTier ?? obj?.tier ?? null;
  return tier ? String(tier) : "single";
}

function readRawPlanKey() {
  const obj = readLocalUser();
  const raw =
    obj?.raw_tier ??
    obj?.rawPlanKey ??
    obj?.plan_key ??
    obj?.planKey ??
    obj?.subscription_plan ??
    obj?.subscriptionPlan ??
    null;
  return raw ? String(raw) : null;
}

function writeSubscriptionTier(nextTier, rawPlanKey = null) {
  try {
    const clean = String(nextTier || "").toLowerCase() === "top" ? "top" : "single";
    const prev = readLocalUser() || {};
    const obj = {
      ...prev,
      subscription_tier: clean,
      raw_tier: rawPlanKey ?? prev?.raw_tier ?? null,
      updated_at_iso: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(obj));
    return clean;
  } catch {
    return String(nextTier || "single");
  }
}

function isTopTier(tier) {
  return String(tier || "").toLowerCase() === "top";
}

function loadBusinessPayload(businessId) {
  try {
    return safeParse(localStorage.getItem(`${STORAGE_KEYS.BUSINESS_PAYLOAD_PREFIX}${businessId}`), null);
  } catch {
    return null;
  }
}

function setActiveBusinessId(businessId) {
  try {
    localStorage.setItem(STORAGE_KEYS.ACTIVE_BUSINESS_ID, String(businessId));
  } catch {
    // ignore
  }
}

function getActiveBusinessId() {
  try {
    return getActiveBusiness()?.business_id || localStorage.getItem(STORAGE_KEYS.ACTIVE_BUSINESS_ID) || "";
  } catch {
    return "";
  }
}

function setConfigShim(payload) {
  try {
    localStorage.setItem(STORAGE_KEYS.CONFIG, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

function readConfigShim() {
  try {
    const cfg = safeParse(localStorage.getItem(STORAGE_KEYS.CONFIG), null);
    return cfg && typeof cfg === "object" ? cfg : null;
  } catch {
    return null;
  }
}

function writeBusinessIndex(rows) {
  try {
    localStorage.setItem(STORAGE_KEYS.BUSINESS_INDEX, JSON.stringify(Array.isArray(rows) ? rows : []));
  } catch {
    // ignore
  }
}

function uniqueStrings(values) {
  const out = [];
  const seen = new Set();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const s = String(value || "").trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  });
  return out;
}

function normalizeBusinessIndexRow(row) {
  if (!row || typeof row !== "object") return null;

  const businessId = String(row.business_id || row.businessId || "").trim();
  if (!businessId) return null;

  const entityName =
    String(row.entity_name || row.entityName || row.clientName || "").trim() || "Untitled Entity";

  return {
    business_id: businessId,
    entity_name: entityName,
    updated_at_iso: row.updated_at_iso || row.created_at_iso || new Date().toISOString(),
    jurisdictions: uniqueStrings(row.jurisdictions),
    industry: String(row.industry || "").trim(),
    niche: String(row.niche || "").trim(),
  };
}

function buildBusinessIndexRow(businessId, payload = null, snapshotRow = null) {
  const cleanBusinessId = String(businessId || "").trim();
  if (!cleanBusinessId) return null;

  const entityName =
    String(
      payload?.clientName ||
        payload?.entity_name ||
        payload?.summary?.entity_name ||
        snapshotRow?.entity_name ||
        ""
    ).trim() || "Untitled Entity";

  return {
    business_id: cleanBusinessId,
    entity_name: entityName,
    updated_at_iso:
      payload?.updated_at_iso ||
      snapshotRow?.updated_at_iso ||
      snapshotRow?.created_at_iso ||
      new Date().toISOString(),
    jurisdictions: uniqueStrings(
      payload?.jurisdictions || payload?.summary?.jurisdictions || snapshotRow?.jurisdictions || []
    ),
    industry: String(payload?.industry || snapshotRow?.industry || "").trim(),
    niche: String(payload?.niche || snapshotRow?.niche || "").trim(),
  };
}

function ensureBusinessPayload(businessId, payload = null, fallbackRow = null) {
  const cleanBusinessId = String(businessId || "").trim();
  if (!cleanBusinessId) return null;

  const existing = loadBusinessPayload(cleanBusinessId);
  if (existing && typeof existing === "object") return existing;

  const nextPayload = {
    business_id: cleanBusinessId,
    clientName:
      payload?.clientName ||
      payload?.entity_name ||
      payload?.summary?.entity_name ||
      fallbackRow?.entity_name ||
      "Untitled Entity",
    entity_name:
      payload?.entity_name || payload?.clientName || fallbackRow?.entity_name || "Untitled Entity",
    jurisdictions: uniqueStrings(
      payload?.jurisdictions || payload?.summary?.jurisdictions || fallbackRow?.jurisdictions || []
    ),
    industry: String(payload?.industry || fallbackRow?.industry || "").trim(),
    niche: String(payload?.niche || fallbackRow?.niche || "").trim(),
    updated_at_iso:
      payload?.updated_at_iso || fallbackRow?.updated_at_iso || fallbackRow?.created_at_iso || new Date().toISOString(),
  };

  try {
    localStorage.setItem(
      `${STORAGE_KEYS.BUSINESS_PAYLOAD_PREFIX}${cleanBusinessId}`,
      JSON.stringify(nextPayload)
    );
  } catch {
    // ignore
  }

  return nextPayload;
}

function readRawSnapshotIndexRows() {
  const keys = [
    "aifine_audit_snapshot_index_v1",
    "aifine_audit_snapshot_index",
    "aifine_audit_snapshot_index_v0",
    "aifine_snapshot_index",
  ];

  const merged = [];
  const seen = new Set();

  keys.forEach((key) => {
    const arr = safeParse(localStorage.getItem(key), []);
    if (!Array.isArray(arr)) return;

    arr.forEach((row) => {
      if (!row || typeof row !== "object") return;
      const snapshotId = String(row.snapshot_id || "").trim();
      if (snapshotId) {
        if (seen.has(snapshotId)) return;
        seen.add(snapshotId);
      }
      merged.push(row);
    });
  });

  return merged.sort((a, b) => {
    const ta = Date.parse(a?.created_at_iso || a?.updated_at_iso || "") || 0;
    const tb = Date.parse(b?.created_at_iso || b?.updated_at_iso || "") || 0;
    return tb - ta;
  });
}

function hydrateBusinessIndexFromStorage() {
  const map = new Map();
  const rawIndex = safeParse(localStorage.getItem(STORAGE_KEYS.BUSINESS_INDEX), []);
  const normalizedIndex = Array.isArray(rawIndex) ? rawIndex.map(normalizeBusinessIndexRow).filter(Boolean) : [];

  normalizedIndex.forEach((row) => {
    map.set(String(row.business_id), row);
  });

  try {
    Object.keys(localStorage).forEach((key) => {
      if (!String(key).startsWith(STORAGE_KEYS.BUSINESS_PAYLOAD_PREFIX)) return;
      const businessId = String(key).slice(STORAGE_KEYS.BUSINESS_PAYLOAD_PREFIX.length).trim();
      if (!businessId) return;
      const payload = loadBusinessPayload(businessId);
      const row = buildBusinessIndexRow(businessId, payload, null);
      if (!row) return;
      map.set(businessId, { ...(map.get(businessId) || {}), ...row });
      ensureBusinessPayload(businessId, payload, row);
    });
  } catch {
    // ignore
  }

  readRawSnapshotIndexRows().forEach((snapshotRow) => {
    const businessId = String(snapshotRow?.business_id || "").trim();
    if (!businessId) return;
    const payload = loadBusinessPayload(businessId);
    const row = buildBusinessIndexRow(businessId, payload, snapshotRow);
    if (!row) return;
    map.set(businessId, { ...(map.get(businessId) || {}), ...row });
    ensureBusinessPayload(businessId, payload, row);
  });

  const cfg = readConfigShim();
  const activeBusinessId = String(getActiveBusinessId() || cfg?.business_id || "").trim();
  if (activeBusinessId) {
    const payload = loadBusinessPayload(activeBusinessId) || cfg || null;
    const row = buildBusinessIndexRow(activeBusinessId, payload, null);
    if (row) {
      map.set(activeBusinessId, { ...(map.get(activeBusinessId) || {}), ...row });
      ensureBusinessPayload(activeBusinessId, payload, row);
    }
  }

  const next = Array.from(map.values())
    .map(normalizeBusinessIndexRow)
    .filter(Boolean)
    .sort((a, b) => {
      const ta = Date.parse(a?.updated_at_iso || "") || 0;
      const tb = Date.parse(b?.updated_at_iso || "") || 0;
      return tb - ta;
    });

  const currentSerialized = JSON.stringify(normalizedIndex);
  const nextSerialized = JSON.stringify(next);
  if (currentSerialized !== nextSerialized) {
    writeBusinessIndex(next);
  }

  return next;
}

function loadBusinessIndex() {
  return hydrateBusinessIndexFromStorage();
}

function jurLabel(j) {
  return Array.isArray(j) && j.length ? j.join(", ") : null;
}

function countAnsweredValues(obj) {
  if (!obj || typeof obj !== "object") return 0;
  let n = 0;
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v === true || v === false || v === "unsure" || v === "YES" || v === "NO" || v === "UNSURE") n += 1;
  }
  return n;
}

function readProgressForBusiness(businessId) {
  try {
    const clean = String(businessId || "").trim();
    if (!clean) return null;
    return safeParse(localStorage.getItem(`${STORAGE_KEYS.PROGRESS_PREFIX}${clean}`), null);
  } catch {
    return null;
  }
}

function readAnswersForBusiness(businessId) {
  try {
    const clean = String(businessId || "").trim();
    if (!clean) return null;
    return safeParse(localStorage.getItem(`${STORAGE_KEYS.ANSWERS_PREFIX}${clean}`), null);
  } catch {
    return null;
  }
}

function clearRunStateForBusiness(businessId) {
  const clean = String(businessId || "").trim();
  if (!clean) return;

  try {
    localStorage.removeItem(`${STORAGE_KEYS.PROGRESS_PREFIX}${clean}`);
  } catch {}

  try {
    localStorage.removeItem(`${STORAGE_KEYS.ANSWERS_PREFIX}${clean}`);
  } catch {}
}

function getIsoMs(value) {
  const t = Date.parse(String(value || ""));
  return Number.isFinite(t) ? t : 0;
}

function shouldShowInProgress({ prog, ans, hasCompletedSnapshot, latestSnapshotIso }) {
  const total = Number(prog?.totalQuestions) || 0;
  const completed = Number(prog?.completedCount) || 0;
  const answeredFallback = countAnsweredValues(ans);

  const isIncompleteByProgress = total > 0 && completed < total;
  const isIncompleteByAnswersOnly = !prog && !hasCompletedSnapshot && answeredFallback > 0;

  if (isIncompleteByAnswersOnly) {
    return {
      show: true,
      completed: answeredFallback,
      total: total || 0,
      resumeAt: null,
    };
  }

  if (!isIncompleteByProgress) {
    return {
      show: false,
      completed,
      total,
      resumeAt: null,
    };
  }

  if (hasCompletedSnapshot) {
    const progressMs = getIsoMs(prog?.updated_at_iso);
    const snapshotMs = getIsoMs(latestSnapshotIso);
    if (snapshotMs && progressMs && progressMs <= snapshotMs) {
      return {
        show: false,
        completed,
        total,
        resumeAt: null,
      };
    }
  }

  return {
    show: true,
    completed,
    total,
    resumeAt: typeof prog?.currentIndex === "number" ? Number(prog.currentIndex) + 1 : null,
  };
}

function clamp(n, lo, hi) {
  const v = Number(n);
  if (!Number.isFinite(v)) return lo;
  return Math.max(lo, Math.min(hi, v));
}

function safePct(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return null;
  return clamp(Math.round(n), 0, 100);
}

function safeNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function formatWhen(iso) {
  try {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

function formatDateOnly(iso) {
  try {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString();
  } catch {
    return "—";
  }
}

function fmtMoney(x, sym = "£") {
  if (x === null || x === undefined || x === "") return "—";
  const v = Number(x);
  if (!Number.isFinite(v)) return "—";
  return `${sym}${Math.round(v).toLocaleString("en-US")}`;
}

function fmtCompactMoney(x, sym = "£") {
  if (x === null || x === undefined || x === "") return "—";
  const v = Number(x);
  if (!Number.isFinite(v)) return "—";
  const abs = Math.abs(v);
  if (abs >= 1000000) return `${sym}${(v / 1000000).toFixed(abs >= 10000000 ? 1 : 2)}M`;
  if (abs >= 1000) return `${sym}${Math.round(v / 1000)}k`;
  return `${sym}${Math.round(v).toLocaleString("en-US")}`;
}

function getRiskGrade(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "—";
  if (n <= 15) return "A";
  if (n <= 30) return "B";
  if (n <= 50) return "C";
  if (n <= 75) return "D";
  return "E";
}

function getRiskBandLabel(score, explicitGrade = "") {
  const normalized = String(explicitGrade || "").trim().toUpperCase();
  const grade = /^[A-E]$/.test(normalized) ? normalized : getRiskGrade(score);
  if (grade === "A") return "Low Risk";
  if (grade === "B") return "Guarded Risk";
  if (grade === "C") return "Moderate Risk";
  if (grade === "D") return "High Risk";
  if (grade === "E") return "Severe Risk";
  return "—";
}

function convertDisplayMoney(amount, fromCurrencyCode, toCurrencyCode, fxCache) {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return null;
  const from = String(fromCurrencyCode || "").trim().toUpperCase();
  const to = String(toCurrencyCode || "").trim().toUpperCase();
  if (!from || !to || from === to) return numeric;
  return convertMoney(numeric, from, to, fxCache);
}

function getExposureOutlook(structuredWorstCase, ceilingTotal) {
  const exposure = safeNum(structuredWorstCase) || 0;
  const ceiling = safeNum(ceilingTotal) || 0;

  if (ceiling > 0) {
    const pct = exposure / ceiling;
    if (pct >= 0.35) {
      return {
        label: "Severe",
        tone: "bg-rose-50 border-rose-200 text-rose-900",
        badge: "bg-rose-600 text-white",
        message: "Modelled exposure is materially elevated relative to the visible legal cap.",
      };
    }
    if (pct >= 0.15) {
      return {
        label: "Elevated",
        tone: "bg-orange-50 border-orange-200 text-orange-900",
        badge: "bg-orange-500 text-white",
        message: "Modelled exposure is meaningfully elevated and should receive executive attention.",
      };
    }
    if (pct >= 0.05) {
      return {
        label: "Moderate",
        tone: "bg-amber-50 border-amber-200 text-amber-950",
        badge: "bg-amber-500 text-white",
        message: "Modelled exposure remains below the legal cap but is large enough to warrant governance focus.",
      };
    }
    return {
      label: "Low",
      tone: "bg-emerald-50 border-emerald-200 text-emerald-950",
      badge: "bg-emerald-600 text-white",
      message: "Modelled exposure is materially below the visible legal cap.",
    };
  }

  if (exposure >= 10000000) {
    return {
      label: "Severe",
      tone: "bg-rose-50 border-rose-200 text-rose-900",
      badge: "bg-rose-600 text-white",
      message: "Modelled exposure is materially elevated.",
    };
  }
  if (exposure >= 1000000) {
    return {
      label: "Elevated",
      tone: "bg-orange-50 border-orange-200 text-orange-900",
      badge: "bg-orange-500 text-white",
      message: "Modelled exposure is meaningfully elevated.",
    };
  }
  if (exposure >= 100000) {
    return {
      label: "Moderate",
      tone: "bg-amber-50 border-amber-200 text-amber-950",
      badge: "bg-amber-500 text-white",
      message: "Modelled exposure remains moderate in absolute terms.",
    };
  }
  return {
    label: "Low",
    tone: "bg-emerald-50 border-emerald-200 text-emerald-950",
    badge: "bg-emerald-600 text-white",
    message: "Modelled exposure remains comparatively limited.",
  };
}

function getTrendSignal(points) {
  const rows = Array.isArray(points) ? points.filter(Boolean) : [];
  if (rows.length < 2) {
    return {
      label: "Stable",
      icon: "→",
      tone: "text-slate-700",
      detail: "Not enough completed audits to infer a directional trend yet.",
    };
  }

  const latest = rows[rows.length - 1];
  const delta = latest?.deltaPct;

  if (!Number.isFinite(delta) || delta === 0) {
    return {
      label: "Stable",
      icon: "→",
      tone: "text-slate-700",
      detail: "Latest higher exposure is broadly unchanged versus the previous completed audit.",
    };
  }

  if (delta < 0) {
    return {
      label: "Improving",
      icon: "↓",
      tone: "text-emerald-600",
      detail: `Higher exposure is down ${Math.abs(delta)}% versus the previous completed audit.`,
    };
  }

  return {
    label: "Increasing",
    icon: "↑",
    tone: "text-amber-600",
    detail: `Higher exposure is up ${Math.abs(delta)}% versus the previous completed audit.`,
  };
}

function useTooltipState() {
  const [open, setOpen] = useState(false);
  return { open, setOpen };
}

function useResizeObserver(ref) {
  const [rect, setRect] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref?.current;
    if (!el) return;
    if (typeof ResizeObserver === "undefined") return;

    let raf = null;
    const ro = new ResizeObserver((entries) => {
      const r = entries?.[0]?.contentRect;
      if (!r) return;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setRect({ width: Math.round(r.width), height: Math.round(r.height) });
      });
    });

    ro.observe(el);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [ref]);

  return rect;
}

function getByPath(obj, path) {
  let cur = obj;
  for (const key of path) {
    if (!cur || typeof cur !== "object") return null;
    cur = cur[key];
  }
  return cur ?? null;
}

function firstNumeric(sources, paths) {
  for (const src of sources) {
    for (const path of paths) {
      const val = safeNum(getByPath(src, path));
      if (val != null) return val;
    }
  }
  return null;
}

function firstString(sources, paths) {
  for (const src of sources) {
    for (const path of paths) {
      const val = getByPath(src, path);
      if (val == null) continue;
      const out = String(val).trim();
      if (out) return out;
    }
  }
  return null;
}

function firstArray(sources, paths) {
  for (const src of sources) {
    for (const path of paths) {
      const val = getByPath(src, path);
      if (Array.isArray(val) && val.length) return val;
    }
  }
  return null;
}

function sumPerRegimeNumeric(sources, key) {
  for (const src of sources) {
    const perRegime = src?.risk_exposure?.per_regime;
    if (!perRegime || typeof perRegime !== "object") continue;

    let total = 0;
    let found = false;

    Object.values(perRegime).forEach((entry) => {
      const value = safeNum(entry?.[key]);
      if (value != null) {
        total += value;
        found = true;
      }
    });

    if (found) return total;
  }
  return null;
}

function deriveExposureFromRiskPayload(payload) {
  const rx = payload?.risk_exposure;
  if (!rx || typeof rx !== "object") {
    return {
      higherExposure: null,
      expectedExposure: null,
      lowerExposure: null,
      ceilingTotal: null,
      currencyCode: null,
      currencySymbol: null,
    };
  }

  const headline = rx?.headline && typeof rx.headline === "object" ? rx.headline : null;
  const byCurrency = rx?.combined_by_currency && typeof rx.combined_by_currency === "object" ? rx.combined_by_currency : null;

  let bucket = null;
  if (headline?.currency_code && byCurrency?.[headline.currency_code]) {
    bucket = byCurrency[headline.currency_code];
  } else if (byCurrency) {
    const firstKey = Object.keys(byCurrency).find(Boolean);
    bucket = firstKey ? byCurrency[firstKey] : null;
  }

  const currencyCode =
    String(
      headline?.currency_code ||
      bucket?.currency_code ||
      payload?.summary?.currency_code ||
      ""
    ).trim().toUpperCase() || null;

  const currencySymbol =
    headline?.currency_symbol ||
    bucket?.currency_symbol ||
    payload?.summary?.currency_symbol ||
    null;

  const ceilingFromHeadline =
    safeNum(headline?.combined_statutory_ceiling_native) ??
    safeNum(rx?.combined_statutory_ceiling_native);

  const ceilingFromRegimes =
    ceilingFromHeadline == null
      ? sumPerRegimeNumeric([payload], "statutory_ceiling_native")
      : null;

  return {
    higherExposure:
      safeNum(headline?.combined_structured_worst_case_native) ??
      safeNum(bucket?.combined_structured_worst_case_native) ??
      safeNum(rx?.combined_structured_worst_case_native) ??
      null,
    expectedExposure:
      safeNum(headline?.combined_net_native) ??
      safeNum(bucket?.combined_net_native) ??
      safeNum(rx?.combined_net_native) ??
      safeNum(headline?.combined_net_planning_case_native) ??
      null,
    lowerExposure:
      safeNum(headline?.combined_floor_native) ??
      safeNum(bucket?.combined_floor_native) ??
      safeNum(rx?.combined_floor_native) ??
      null,
    ceilingTotal: ceilingFromHeadline ?? ceilingFromRegimes ?? null,
    currencyCode,
    currencySymbol,
  };
}

function deriveMode(detail) {
  const raw = firstString(
    [detail?.payload, detail?.latestPayload, detail?.latestIndexRow],
    [
      ["mode"],
      ["analysis_mode"],
      ["summary", "mode"],
      ["report", "mode"],
      ["report_context", "mode"],
    ]
  );
  if (!raw) return "Executive";
  return String(raw).toLowerCase().includes("legal") ? "Legal" : "Executive";
}

function deriveExposureSummary(detail) {
  const sources = [detail?.latestPayload, detail?.latestIndexRow, detail?.payload].filter(Boolean);

  const fromRiskField = (field) => {
    for (const src of sources) {
      const val = deriveExposureFromRiskPayload(src)?.[field];
      if (val != null) return val;
    }
    return null;
  };

  const structuredWorstCase = fromRiskField("higherExposure") ?? firstNumeric(sources, [
    ["structured_worst_case"],
    ["structuredWorstCase"],
    ["risk_exposure", "structured_worst_case"],
    ["riskExposure", "structuredWorstCase"],
    ["risk_exposure", "headline", "combined_structured_worst_case_native"],
    ["risk_exposure_summary", "structured_worst_case"],
    ["report_summary", "structured_worst_case"],
    ["summary", "structured_worst_case"],
    ["report", "structured_worst_case"],
    ["exposure", "structured_worst_case"],
    ["worst_case"],
  ]);

  const netPlanningCase = fromRiskField("expectedExposure") ?? firstNumeric(sources, [
    ["net_planning_case"],
    ["netPlanningCase"],
    ["risk_exposure", "net_planning_case"],
    ["riskExposure", "netPlanningCase"],
    ["risk_exposure", "headline", "combined_net_native"],
    ["risk_exposure", "headline", "combined_net_planning_case_native"],
    ["risk_exposure_summary", "net_planning_case"],
    ["report_summary", "net_planning_case"],
    ["summary", "net_planning_case"],
    ["report", "net_planning_case"],
    ["planning_case"],
  ]);

  const planningFloor = fromRiskField("lowerExposure") ?? firstNumeric(sources, [
    ["planning_floor"],
    ["planningFloor"],
    ["risk_exposure", "planning_floor"],
    ["riskExposure", "planningFloor"],
    ["risk_exposure", "headline", "combined_floor_native"],
    ["risk_exposure_summary", "planning_floor"],
    ["report_summary", "planning_floor"],
    ["summary", "planning_floor"],
    ["report", "planning_floor"],
    ["floor"],
  ]);

  const globalCeilingTotal =
    fromRiskField("ceilingTotal") ??
    firstNumeric(sources, [
      ["global_ceiling_total"],
      ["globalCeilingTotal"],
      ["statutory_ceiling_total"],
      ["statutoryCeilingTotal"],
      ["risk_exposure", "global_ceiling_total"],
      ["riskExposure", "globalCeilingTotal"],
      ["risk_exposure_summary", "global_ceiling_total"],
      ["report_summary", "global_ceiling_total"],
      ["summary", "global_ceiling_total"],
      ["report", "global_ceiling_total"],
    ]) ??
    sumPerRegimeNumeric(sources, "statutory_ceiling_native");

  const currencySymbol =
    sources.map((src) => deriveExposureFromRiskPayload(src)?.currencySymbol).find(Boolean) ||
    firstString(sources, [["summary", "currency_symbol"]]) ||
    "€";

  const currencyCode =
    sources.map((src) => deriveExposureFromRiskPayload(src)?.currencyCode).find(Boolean) ||
    firstString(sources, [["summary", "currency_code"]]) ||
    "";

  return {
    structuredWorstCase,
    netPlanningCase,
    planningFloor,
    globalCeilingTotal,
    currencySymbol,
    currencyCode,
  };
}

function buildSnapshotDetail({ businessId, payload, indexRows }) {
  const rows = Array.isArray(indexRows) ? indexRows.slice() : [];
  const latestIndexRow = rows.length ? rows[0] : null;
  const latestPayload = latestIndexRow?.snapshot_id ? getAuditSnapshotPayload(String(latestIndexRow.snapshot_id)) : null;
  const sources = [latestPayload, latestIndexRow, payload].filter(Boolean);

  const exposure = deriveExposureSummary({ latestPayload, latestIndexRow, payload });

  const jurisdictions =
    firstArray(sources, [["jurisdictions"], ["summary", "jurisdictions"], ["config", "jurisdictions"]]) || [];

  const entityName =
    firstString(sources, [["clientName"], ["entity_name"], ["summary", "entity_name"], ["config", "clientName"]]) ||
    "Unnamed";

  return {
    businessId,
    payload,
    latestIndexRow,
    latestPayload,
    entityName,
    jurisdictions,
    mode: deriveMode({ payload, latestPayload, latestIndexRow }),
    createdAtIso: latestIndexRow?.created_at_iso || null,
    riskScore:
      safePct(getByPath(latestPayload, ["assessment", "severity_score_0_100"])) ??
      safePct(latestIndexRow?.severity_score_0_100) ??
      null,
    riskGrade:
      firstString(sources, [["assessment", "risk_grade"], ["risk_grade"]]) || null,
    score: safePct(latestIndexRow?.compliance_score_pct),
    failed: safeNum(latestIndexRow?.failed_count) ?? safeNum(getByPath(latestPayload, ["assessment", "failed_count"])) ?? 0,
    unsure:
      safeNum(latestIndexRow?.unsure_count) ?? safeNum(getByPath(latestPayload, ["assessment", "unsure_count"])) ?? 0,
    ...exposure,
  };
}

function buildTrendPoints(indexRows) {
  const rows = Array.isArray(indexRows) ? indexRows.slice().reverse() : [];
  const all = rows
    .map((row) => {
      const created = row?.created_at_iso || null;
      const t = Date.parse(created || "");
      if (!Number.isFinite(t)) return null;

      const payload = row?.snapshot_id ? getAuditSnapshotPayload(String(row.snapshot_id)) : null;
      const exposure = deriveExposureSummary({ latestPayload: payload, latestIndexRow: row, payload: null });
      const exposureValue = exposure.structuredWorstCase;
      if (exposureValue == null) return null;

      return {
        snapshot_id: String(row?.snapshot_id || ""),
        created_at_iso: created,
        exposure: exposureValue,
        score: safePct(row?.compliance_score_pct),
        t,
      };
    })
    .filter(Boolean);

  return all.slice(-6).map((point, idx, arr) => {
    if (idx === 0) return { ...point, deltaPct: null };
    const prev = arr[idx - 1]?.exposure;
    if (!Number.isFinite(prev) || prev === 0) return { ...point, deltaPct: null };
    return {
      ...point,
      deltaPct: Math.round(((point.exposure - prev) / prev) * 100),
    };
  });
}

function ExposureTrendChart({ points, currencySymbol = "€", trendSignal }) {
  const containerRef = useRef(null);
  const { width } = useResizeObserver(containerRef);

  const chartPoints = useMemo(() => (Array.isArray(points) ? points : []).filter(Boolean), [points]);
  const hasData = chartPoints.length >= 2;

  const svgW = Math.max(620, width || 0);
  const h = 330;
  const padL = 72;
  const padR = 64;
  const padT = 30;
  const padB = 56;

  const domain = useMemo(() => {
    if (!hasData) return null;
    const xs = chartPoints.map((p) => p.t).filter((x) => Number.isFinite(x));
    const ys = chartPoints.map((p) => p.exposure).filter((y) => Number.isFinite(y));
    const minX = xs.length ? Math.min(...xs) : 0;
    const maxX = xs.length ? Math.max(...xs) : 1;
    const minYRaw = ys.length ? Math.min(...ys) : 0;
    const maxYRaw = ys.length ? Math.max(...ys) : 1;
    const pad = Math.max(5000, Math.round((maxYRaw - minYRaw || maxYRaw || 1) * 0.12));
    const minY = Math.max(0, minYRaw - pad);
    const maxY = Math.max(minY + 1, maxYRaw + pad);
    return { minX, maxX, minY, maxY };
  }, [hasData, chartPoints]);

  const xOf = useCallback(
    (t, i) => {
      if (!domain) return padL;
      const span = domain.maxX - domain.minX;
      if (span <= 0) {
        const u = chartPoints.length <= 1 ? 0 : i / (chartPoints.length - 1);
        return padL + u * (svgW - padL - padR);
      }
      const u = (t - domain.minX) / span;
      return padL + clamp(u, 0, 1) * (svgW - padL - padR);
    },
    [domain, chartPoints.length, svgW]
  );

  const yOf = useCallback(
    (value) => {
      if (!domain) return h - padB;
      const span = domain.maxY - domain.minY;
      const u = span <= 0 ? 0 : (value - domain.minY) / span;
      return padT + (1 - clamp(u, 0, 1)) * (h - padT - padB);
    },
    [domain]
  );

  const pathD = useMemo(() => {
    if (!hasData || !domain) return "";
    return chartPoints
      .map((p, i) => `${i === 0 ? "M" : "L"} ${xOf(p.t, i).toFixed(2)} ${yOf(p.exposure).toFixed(2)}`)
      .join(" ");
  }, [hasData, domain, chartPoints, xOf, yOf]);

  const yTicks = useMemo(() => {
    if (!domain) return [];
    const desired = 5;
    const step = (domain.maxY - domain.minY) / (desired - 1);
    return Array.from({ length: desired }, (_, i) => Math.round(domain.minY + i * step));
  }, [domain]);

  const sameDayLabels = useMemo(() => {
    if (!chartPoints.length) return false;
    const normalized = chartPoints
      .map((point) => {
        try {
          return new Date(point.created_at_iso).toLocaleDateString();
        } catch {
          return String(point.created_at_iso || "");
        }
      })
      .filter(Boolean);
    return normalized.length > 1 && new Set(normalized).size === 1;
  }, [chartPoints]);

  const xTicks = useMemo(() => {
    if (!domain || chartPoints.length < 2) return [];

    const candidates = chartPoints.map((point, idx) => ({ idx, point, x: xOf(point.t, idx) }));
    const out = [];
    let lastPlacedX = null;
    const minSpacing = sameDayLabels ? 110 : 90;

    candidates.forEach((item, idx) => {
      const isFirst = idx === 0;
      const isLast = idx === candidates.length - 1;
      if (isFirst || isLast) {
        out.push(item);
        lastPlacedX = item.x;
        return;
      }
      if (lastPlacedX == null || item.x - lastPlacedX >= minSpacing) {
        out.push(item);
        lastPlacedX = item.x;
      }
    });

    const deduped = [];
    const seen = new Set();
    out.forEach((item) => {
      if (seen.has(item.idx)) return;
      seen.add(item.idx);
      deduped.push(item);
    });

    return deduped;
  }, [domain, chartPoints, sameDayLabels, xOf]);


  const formatXAxisLabel = useCallback(
    (iso) => {
      try {
        if (!iso) return "—";
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "—";
        return sameDayLabels
          ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
          : d.toLocaleDateString();
      } catch {
        return "—";
      }
    },
    [sameDayLabels]
  );

  const pointLabelLayout = useMemo(() => {
    const layouts = chartPoints.map((point, idx) => {
      const x = xOf(point.t, idx);
      const y = yOf(point.exposure);
      const prev = idx > 0 ? chartPoints[idx - 1] : null;
      const next = idx < chartPoints.length - 1 ? chartPoints[idx + 1] : null;
      const prevX = prev ? xOf(prev.t, idx - 1) : null;
      const nextX = next ? xOf(next.t, idx + 1) : null;
      const isCrowdedLeft = prevX != null && x - prevX < 90;
      const isCrowdedRight = nextX != null && nextX - x < 90;
      const isCrowded = isCrowdedLeft || isCrowdedRight;
      const dy = isCrowded ? (idx % 2 === 0 ? -16 : -28) : -12;
      const dx = isCrowded ? (idx % 2 === 0 ? -6 : 6) : 0;
      const anchor = idx === 0 ? "start" : idx === chartPoints.length - 1 ? "end" : "middle";
      return { idx, x, y, dx, dy, anchor, showLabel: true };
    });

    for (let i = 1; i < layouts.length - 1; i += 1) {
      const current = layouts[i];
      if (!current?.showLabel) continue;
      const prevShown = layouts.slice(0, i).reverse().find((item) => item?.showLabel);
      if (!prevShown) continue;
      const labelXGap = Math.abs((current.x + (current.dx || 0)) - (prevShown.x + (prevShown.dx || 0)));
      const labelYGap = Math.abs((current.y + (current.dy || 0)) - (prevShown.y + (prevShown.dy || 0)));
      if (labelXGap < 42 && labelYGap < 18) {
        current.showLabel = false;
      }
    }

    return layouts;
  }, [chartPoints, xOf, yOf]);

  return (
    <div ref={containerRef} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.35em] text-slate-400">Risk trend</div>
          <div className="mt-2 text-xl font-extrabold tracking-tight text-slate-900">Higher Exposure Trend</div>
          <div className="mt-1 text-sm text-slate-600">Up to the last 6 completed audits for the active entity.</div>
        </div>

        <div className="text-right">
          <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400">Trend Direction</div>
          <div className={`mt-2 text-sm font-extrabold ${trendSignal?.tone || "text-slate-700"}`}>
            {trendSignal?.icon || "→"} {trendSignal?.label || "Stable"}
          </div>
          <div className="mt-1 max-w-[240px] text-xs text-slate-500">{trendSignal?.detail || "—"}</div>
        </div>
      </div>

      <div className="mt-5">
        {!hasData ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
            No completed audits with exposure data yet. Complete at least two audits to see the trend.
          </div>
        ) : (
          <div className="relative">
            <svg width={svgW} height={h} className="block w-full select-none">
              {yTicks.map((tick) => {
                const y = yOf(tick);
                return (
                  <g key={`y-${tick}`}>
                    <line x1={padL} x2={svgW - padR} y1={y} y2={y} stroke="rgba(15,23,42,0.08)" strokeWidth="1" />
                    <text x={padL - 12} y={y + 4} textAnchor="end" fontSize="11" fill="rgba(15,23,42,0.55)">
                      {fmtCompactMoney(tick, currencySymbol)}
                    </text>
                  </g>
                );
              })}

              <line x1={padL} x2={svgW - padR} y1={h - padB} y2={h - padB} stroke="rgba(15,23,42,0.18)" strokeWidth="1" />

              {xTicks.map(({ idx, point }) => {
                const x = xOf(point.t, idx);
                const anchor = idx === 0 ? "start" : idx === chartPoints.length - 1 ? "end" : "middle";
                return (
                  <g key={`x-${point.snapshot_id || idx}`}>
                    <line x1={x} x2={x} y1={h - padB} y2={h - padB + 6} stroke="rgba(15,23,42,0.25)" strokeWidth="1" />
                    <text x={x} y={h - padB + 24} textAnchor={anchor} fontSize="11" fill="rgba(15,23,42,0.55)">
                      {formatXAxisLabel(point.created_at_iso)}
                    </text>
                  </g>
                );
              })}

              <path d={pathD} fill="none" stroke="#2563EB" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />

              {chartPoints.map((point, idx) => {
                const layout = pointLabelLayout[idx] || {};
                const x = layout.x ?? xOf(point.t, idx);
                const y = layout.y ?? yOf(point.exposure);
                return (
                  <g key={point.snapshot_id || idx}>
                    {layout.showLabel !== false ? (
                      <text
                        x={x + (layout.dx || 0)}
                        y={y + (layout.dy || -12)}
                        textAnchor={layout.anchor || (idx === 0 ? "start" : idx === chartPoints.length - 1 ? "end" : "middle")}
                        fontSize="11"
                        fontWeight="700"
                        fill="#0F172A"
                      >
                        {fmtCompactMoney(point.exposure, currencySymbol)}
                      </text>
                    ) : null}
                    <circle cx={x} cy={y} r={4} fill="#2563EB" />
                  </g>
                );
              })}
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}

function riskStatusMeta(grade) {
  const g = String(grade || "").trim().toUpperCase();
  if (g === "A") return { label: "Low Risk", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" };
  if (g === "B") return { label: "Guarded Risk", cls: "border-amber-200 bg-amber-50 text-amber-700" };
  if (g === "C") return { label: "Elevated Risk", cls: "border-orange-200 bg-orange-50 text-orange-700" };
  if (g === "D" || g === "E") return { label: "High Risk", cls: "border-rose-200 bg-rose-50 text-rose-700" };
  return { label: "Not Scored", cls: "border-slate-200 bg-slate-50 text-slate-500" };
}

function OverviewRow({ row, active, onSelect }) {
  const status = riskStatusMeta(row?.riskGrade);
  return (
    <tr className={`border-t border-slate-100 ${active ? "bg-emerald-50/70" : "bg-white"}`}>
      <td className="px-4 py-3">
        <div className="font-semibold text-slate-900">{row.entityName}</div>
      </td>
      <td className="px-4 py-3 text-sm text-slate-700">{jurLabel(row.jurisdictions) || "—"}</td>
      <td className="px-4 py-3 text-sm text-slate-700">{formatDateOnly(row.createdAtIso)}</td>
      <td className="px-4 py-3 text-sm font-semibold text-slate-900">{typeof row.riskScore === "number" ? Math.round(row.riskScore) : "—"}</td>
      <td className="px-4 py-3">
        <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-black ${status.cls}`}>{status.label}</span>
      </td>
      <td className="px-4 py-3 text-right">
        {active ? (
          <span className="inline-flex min-w-[84px] items-center justify-center rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-black text-emerald-700">Current</span>
        ) : (
          <button
            type="button"
            onClick={onSelect}
            className="inline-flex min-w-[84px] items-center justify-center rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-black text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
          >
            Select
          </button>
        )}
      </td>
    </tr>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const entitlement = useEntitlement?.() || {};
  const entLoading = Boolean(entitlement.loading);
  const entTierRaw = entitlement.tier != null ? String(entitlement.tier) : null;
  const entPlanKey = entitlement.rawPlanKey ?? entitlement.planKey ?? entitlement.plan_key ?? entitlement.plan ?? null;
  const allowRemediation = Boolean(entitlement.allowRemediation);
  const tooltip = useTooltipState();
  const currentRiskPositionRef = useRef(null);

  const [tier, setTier] = useState(() => {
    try {
      return readSubscriptionTier();
    } catch {
      return "single";
    }
  });

  const [activePayload, setActivePayload] = useState(null);
  const [activeBusinessIdState, setActiveBusinessIdState] = useState(() => getActiveBusinessId() || "");
  const [inProgressRows, setInProgressRows] = useState([]);
  const [trendPoints, setTrendPoints] = useState([]);
  const [latestSnapshot, setLatestSnapshot] = useState(null);
  const [businessRows, setBusinessRows] = useState([]);
  const [displayCurrency, setDisplayCurrency] = useState("USD");
  const [fxCache, setFxCache] = useState(null);

  useEffect(() => {
    if (entLoading) return;

    const inferred = isTopTier(entTierRaw) ? "top" : "single";
    const rawPlanKeyForStorage = entPlanKey != null ? String(entPlanKey) : readRawPlanKey();

    if (String(inferred).toLowerCase() !== String(tier).toLowerCase()) {
      const persisted = writeSubscriptionTier(inferred, rawPlanKeyForStorage);
      setTier(persisted);
    } else {
      writeSubscriptionTier(tier, rawPlanKeyForStorage);
    }
  }, [entLoading, entTierRaw, entPlanKey, tier]);

  useEffect(() => {
    let cancelled = false;

    getCachedFxRates({ maxAgeHours: 12 }).then((cache) => {
      if (cancelled) return;
      if (cache && typeof cache === "object") setFxCache(cache);
    }).catch(() => {
      if (!cancelled) setFxCache(null);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const topTier = useMemo(() => isTopTier(tier), [tier]);

  const effectiveBusinessId = useMemo(() => {
    const canonicalActiveBusinessId = getActiveBusiness()?.business_id || getActiveBusinessId() || "";
    if (topTier) return activeBusinessIdState || canonicalActiveBusinessId || "";
    return activeBusinessIdState || canonicalActiveBusinessId || STANDARD_SINGLE_ID;
  }, [topTier, activeBusinessIdState]);

  const displayBusinessRows = useMemo(() => {
    if (!topTier || !businessRows.length) return businessRows;
    const current = businessRows.find((row) => row.businessId === effectiveBusinessId);
    if (!current) return businessRows;
    return [current, ...businessRows.filter((row) => row.businessId !== effectiveBusinessId)];
  }, [topTier, businessRows, effectiveBusinessId]);

  const buildBusinessRow = useCallback((businessId, payload = null, indexRows = null) => {
    const canonicalLatest = getLatestSnapshotForBusiness({ businessId });
    const resolvedPayload = payload || canonicalLatest?.payload || loadBusinessPayload(businessId) || null;
    let rows = Array.isArray(indexRows) ? indexRows : readAuditSnapshotIndex({ businessId });

    if ((!Array.isArray(rows) || rows.length === 0) && canonicalLatest?.row) {
      rows = [canonicalLatest.row];
    }

    if (!Array.isArray(rows) || rows.length === 0) {
      try {
        rebuildAuditSnapshotIndexFromPayloads();
        rows = readAuditSnapshotIndex({ businessId });
        if ((!Array.isArray(rows) || rows.length === 0) && canonicalLatest?.row) {
          rows = [canonicalLatest.row];
        }
      } catch {
        rows = rows || [];
      }
    }

    return buildSnapshotDetail({ businessId, payload: resolvedPayload, indexRows: rows });
  }, []);

  const refreshSnapshots = useCallback(() => {
    try {
      const detail = buildBusinessRow(effectiveBusinessId, activePayload, null);
      const rows = readAuditSnapshotIndex({ businessId: effectiveBusinessId });
      const points = buildTrendPoints(rows);
      setTrendPoints(points);
      setLatestSnapshot(detail);
    } catch {
      setTrendPoints([]);
      setLatestSnapshot(null);
    }
  }, [effectiveBusinessId, activePayload, buildBusinessRow]);

  const refreshBusinessState = useCallback(() => {
    if (topTier) {
      hydrateBusinessIndexFromStorage();
    }

    if (!topTier) {
      const active = getActiveBusiness()?.business_id || getActiveBusinessId();
      const resolvedBid = active || STANDARD_SINGLE_ID;

      if (!active) {
        setActiveBusinessId(resolvedBid);
      }
      setActiveBusinessIdState(resolvedBid);

      const cfg = safeParse(localStorage.getItem(STORAGE_KEYS.CONFIG), null);
      const payload = loadBusinessPayload(resolvedBid) || cfg || null;
      setActivePayload(payload || null);

      const prog = readProgressForBusiness(resolvedBid);
      const ans = readAnswersForBusiness(resolvedBid);
      const snapshotRows = readAuditSnapshotIndex({ businessId: resolvedBid });
      const hasCompletedSnapshot = Array.isArray(snapshotRows) && snapshotRows.length > 0;

      const progressState = shouldShowInProgress({
        prog,
        ans,
        hasCompletedSnapshot,
        latestSnapshotIso: Array.isArray(snapshotRows) && snapshotRows[0]?.created_at_iso ? snapshotRows[0].created_at_iso : null,
      });

      if (progressState.show) {
        setInProgressRows([
          {
            business_id: resolvedBid,
            entity_name: payload?.clientName || "Unnamed",
            jurisdictions: payload?.jurisdictions || [],
            completed: progressState.completed,
            total: progressState.total,
            resumeAt: progressState.resumeAt,
            updated_at_iso: prog?.updated_at_iso || payload?.updated_at_iso || null,
          },
        ]);
      } else {
        setInProgressRows([]);
      }

      setBusinessRows([]);
      return;
    }

    const idx = loadBusinessIndex();
    const activeId = getActiveBusiness()?.business_id || getActiveBusinessId();

    let resolvedActiveId = activeId;
    if (!resolvedActiveId && idx.length) {
      const first = idx[0]?.business_id;
      if (first) {
        setActiveBusinessId(first);
        setActiveBusinessIdState(first);
        resolvedActiveId = first;
      }
    }

    setActiveBusinessIdState(resolvedActiveId || "");
    const active = resolvedActiveId ? loadBusinessPayload(resolvedActiveId) : null;
    setActivePayload(active || null);

    const rows = [];
    const inProgress = [];

    for (const entry of idx) {
      const bid = entry?.business_id;
      if (!bid) continue;

      const payload = loadBusinessPayload(bid);
      const builtRow = buildBusinessRow(bid, payload, null);
      rows.push(builtRow);

      const prog = readProgressForBusiness(bid);
      const ans = readAnswersForBusiness(bid);
      const hasCompletedSnapshot = Boolean(builtRow?.latestIndexRow?.snapshot_id);

      const progressState = shouldShowInProgress({
        prog,
        ans,
        hasCompletedSnapshot,
        latestSnapshotIso: builtRow?.createdAtIso || null,
      });

      if (progressState.show) {
        inProgress.push({
          business_id: bid,
          entity_name: payload?.clientName || entry.entity_name || "Unnamed",
          jurisdictions: payload?.jurisdictions || entry.jurisdictions || [],
          completed: progressState.completed,
          total: progressState.total,
          resumeAt: progressState.resumeAt,
          updated_at_iso: prog?.updated_at_iso || payload?.updated_at_iso || entry.updated_at_iso || null,
        });
      }
    }

    inProgress.sort((a, b) => {
      const ta = a.updated_at_iso ? new Date(a.updated_at_iso).getTime() : 0;
      const tb = b.updated_at_iso ? new Date(b.updated_at_iso).getTime() : 0;
      return tb - ta;
    });

    rows.sort((a, b) => {
      const ea = a?.structuredWorstCase ?? -1;
      const eb = b?.structuredWorstCase ?? -1;
      return eb - ea;
    });

    setBusinessRows(rows);
    setInProgressRows(inProgress);
  }, [topTier, buildBusinessRow]);

  useEffect(() => {
    refreshBusinessState();
  }, [refreshBusinessState]);

  useEffect(() => {
    refreshSnapshots();
  }, [refreshSnapshots]);

  useEffect(() => {
    const onFocus = () => refreshBusinessState();

    const onStorage = (e) => {
      if (!e || e.key == null) return;
      const k = String(e.key);

      if (k === STORAGE_KEYS.ACTIVE_BUSINESS_ID) return refreshBusinessState();
      if (k === STORAGE_KEYS.BUSINESS_INDEX) return refreshBusinessState();
      if (k.startsWith(STORAGE_KEYS.BUSINESS_PAYLOAD_PREFIX)) return refreshBusinessState();
      if (k.startsWith(STORAGE_KEYS.PROGRESS_PREFIX)) return refreshBusinessState();
      if (k.startsWith(STORAGE_KEYS.ANSWERS_PREFIX)) return refreshBusinessState();
      if (k === STORAGE_KEYS.CONFIG && !topTier) return refreshBusinessState();
      if (k === "aifine_audit_snapshot_index_v1") return refreshBusinessState();
      if (k.startsWith("aifine_audit_snapshot_payload_")) return refreshBusinessState();
      if (k.startsWith("aifine_audit_snapshot_payload_snap_")) return refreshBusinessState();
    };

    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
    };
  }, [topTier, refreshBusinessState]);

  const activeEntityName = latestSnapshot?.entityName || activePayload?.clientName || null;
  const activeJurisdictions = latestSnapshot?.jurisdictions || activePayload?.jurisdictions || [];
  const activeJur = jurLabel(activeJurisdictions);
  const latestScore = latestSnapshot?.score;
  const latestWhen = latestSnapshot?.createdAtIso;
  const activeMode = latestSnapshot?.mode || "Executive";
  const latestCurrencySymbol = latestSnapshot?.currencySymbol || "€";
  const latestCurrencyCode = String(latestSnapshot?.currencyCode || activePayload?.currency_code || "USD").trim().toUpperCase() || "USD";

  useEffect(() => {
    if (!latestCurrencyCode) return;
    setDisplayCurrency(latestCurrencyCode);
  }, [latestCurrencyCode]);

  const availableCurrencies = useMemo(() => {
    const set = new Set([latestCurrencyCode || "USD", "USD", "EUR", "GBP"]);
    return Array.from(set).filter(Boolean);
  }, [latestCurrencyCode]);

  const displayCurrencySymbol = guessCurrencySymbol(displayCurrency || latestCurrencyCode || "USD");
  const activeRiskScore = latestSnapshot?.riskScore;
  const activeRiskGrade = latestSnapshot?.riskGrade || getRiskGrade(activeRiskScore);
  const activeRiskBandLabel = getRiskBandLabel(activeRiskScore, activeRiskGrade);

  const displayedSnapshot = useMemo(() => {
    if (!latestSnapshot) return null;
    const fromCode = latestSnapshot?.currencyCode || latestCurrencyCode || displayCurrency;
    return {
      ...latestSnapshot,
      structuredWorstCase: convertDisplayMoney(latestSnapshot?.structuredWorstCase, fromCode, displayCurrency, fxCache),
      netPlanningCase: convertDisplayMoney(latestSnapshot?.netPlanningCase, fromCode, displayCurrency, fxCache),
      planningFloor: convertDisplayMoney(latestSnapshot?.planningFloor, fromCode, displayCurrency, fxCache),
      globalCeilingTotal: convertDisplayMoney(latestSnapshot?.globalCeilingTotal, fromCode, displayCurrency, fxCache),
      currencyCode: displayCurrency,
      currencySymbol: displayCurrencySymbol,
    };
  }, [latestSnapshot, latestCurrencyCode, displayCurrency, fxCache, displayCurrencySymbol]);

  const displayedTrendPoints = useMemo(() => {
    if (!Array.isArray(trendPoints) || !trendPoints.length) return [];
    return trendPoints.map((point) => ({
      ...point,
      exposure: convertDisplayMoney(point?.exposure, latestCurrencyCode, displayCurrency, fxCache),
    }));
  }, [trendPoints, latestCurrencyCode, displayCurrency, fxCache]);

  const portfolio = useMemo(() => {
    if (!topTier || !businessRows.length) return null;
    const entitiesMonitored = businessRows.length;
    const totalPotentialExposure = businessRows.reduce((sum, row) => sum + (row?.structuredWorstCase || 0), 0);
    const withExposure = businessRows.filter((row) => Number.isFinite(row?.structuredWorstCase));
    const highestExposureEntity = withExposure.length
      ? withExposure.reduce((best, row) => ((row.structuredWorstCase || 0) > (best?.structuredWorstCase || 0) ? row : best), withExposure[0])
      : null;
    const scores = businessRows.map((row) => row?.score).filter((v) => typeof v === "number");
    const averageRiskScore = scores.length ? Math.round(scores.reduce((sum, v) => sum + v, 0) / scores.length) : null;

    return {
      entitiesMonitored,
      totalPotentialExposure,
      highestExposureEntity: highestExposureEntity?.entityName || "—",
      averageRiskScore,
    };
  }, [topTier, businessRows]);

  const heroCards = [
    { label: "Worst Case", value: fmtMoney(displayedSnapshot?.structuredWorstCase, displayCurrencySymbol), tone: "text-rose-600" },
    { label: "Expected Exposure", value: fmtMoney(displayedSnapshot?.netPlanningCase, displayCurrencySymbol), tone: "text-slate-900" },
    { label: "Floor", value: fmtMoney(displayedSnapshot?.planningFloor, displayCurrencySymbol), tone: "text-emerald-600" },
  ];

  const legalCapValue = fmtMoney(displayedSnapshot?.globalCeilingTotal, displayCurrencySymbol);
  const exposureOutlook = getExposureOutlook(displayedSnapshot?.structuredWorstCase, displayedSnapshot?.globalCeilingTotal);
  const trendSignal = getTrendSignal(displayedTrendPoints);
  const entitiesMonitoredCount = topTier ? businessRows.length : (latestSnapshot ? 1 : 0);
  const showFirstRunHero = !latestSnapshot?.latestIndexRow?.snapshot_id && inProgressRows.length === 0 && entitiesMonitoredCount === 0;

  const handleDeleteInProgress = useCallback(
    (businessId, label) => {
      const name = label || "this in-progress assessment";
      const ok = window.confirm(
        `Delete ${name}?\n\nThis removes only saved in-progress answers/progress for this business. Completed audits and reports are not deleted.`
      );
      if (!ok) return;
      clearRunStateForBusiness(businessId);
      refreshBusinessState();
    },
    [refreshBusinessState]
  );

  const handleSelectBusiness = useCallback(
    (businessId, options = {}) => {
      const nextBusinessId = String(businessId || "").trim();
      if (!nextBusinessId) return;

      setActiveBusinessId(nextBusinessId);
      setActiveBusinessIdState(nextBusinessId);

      const payload = ensureBusinessPayload(nextBusinessId, loadBusinessPayload(nextBusinessId), null);
      if (payload) {
        setConfigShim(payload);
        setActivePayload(payload);
      } else {
        setActivePayload(null);
      }

      try {
        const rows = readAuditSnapshotIndex({ businessId: nextBusinessId });
        setTrendPoints(buildTrendPoints(rows));
        setLatestSnapshot(buildBusinessRow(nextBusinessId, payload, rows));
      } catch {
        setTrendPoints([]);
        setLatestSnapshot(null);
      }

      if (options?.scrollToRisk) {
        window.requestAnimationFrame(() => {
          try {
            currentRiskPositionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          } catch {
            try {
              currentRiskPositionRef.current?.scrollIntoView();
            } catch {}
          }
        });
      }
    },
    [buildBusinessRow]
  );

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 text-slate-900">
      <Sidebar />

      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-7xl px-8 py-10">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
                {!topTier ? (
                  <p className="mt-1 text-sm text-slate-600">Run a new Assessment Audit or resume an in-progress audit.</p>
                ) : null}

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {activeEntityName ? (
                    <span className="rounded-xl border border-blue-600 bg-blue-600 px-4 py-2 text-sm font-black text-white shadow-sm">
                      Entity: {activeEntityName}
                    </span>
                  ) : null}
                  {activeJur ? (
                    <span className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-extrabold text-slate-800 shadow-sm">
                      Scope: {activeJur}
                    </span>
                  ) : null}
                  <span className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-extrabold text-slate-800 shadow-sm">
                    Mode: {activeMode}
                  </span>
                  <span className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-extrabold text-slate-800 shadow-sm">
                    Created: {latestWhen ? formatDateOnly(latestWhen) : "—"}
                  </span>
                </div>
              </div>

              <div className="flex flex-col items-stretch gap-3 sm:items-end">
                <div className="flex items-center gap-2">
                  {topTier ? (
                    <button
                      type="button"
                      onClick={() => navigate("/start-audit?mode=new")}
                      className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                    >
                      + New Business Audit →
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            {showFirstRunHero ? (
              <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
                <div className="max-w-3xl">
                  <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-400">Get Started</div>
                  <h2 className="mt-3 text-2xl font-extrabold tracking-tight text-slate-900">Understand Your Regulatory Risk Exposure</h2>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    AI Fine Protector estimates your potential financial exposure to regulatory fines based on your organization’s AI risk profile.
                  </p>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    Run your first audit to generate your Risk Score and exposure analysis.
                  </p>
                  <div className="mt-6">
                    <button
                      type="button"
                      onClick={() => navigate(topTier ? "/start-audit?mode=new" : "/start-audit?mode=rerun")}
                      className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800"
                    >
                      Start First Audit
                    </button>
                  </div>
                </div>
              </div>
            ) : null}


            {topTier ? (
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className="text-xs font-black uppercase tracking-[0.35em] text-slate-400">Entities Overview</div>
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <div className="text-xl font-extrabold tracking-tight text-slate-900">Latest position by entity</div>
                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                        Entities Monitored • {entitiesMonitoredCount}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 shadow-sm">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400">Reporting Currency</div>
                      <div className="mt-1 text-xs text-slate-500">Display currency only — underlying calculations are unchanged.</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <select
                        value={displayCurrency}
                        onChange={(e) => setDisplayCurrency(e.target.value)}
                        className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm outline-none focus:border-slate-400"
                      >
                        {availableCurrencies.map((code) => (
                          <option key={code} value={code}>
                            {code}
                          </option>
                        ))}
                      </select>
                      <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                        All values reflect selected currency
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200">
                  <table className="min-w-full border-collapse text-sm">
                    <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">
                      <tr>
                        <th className="px-4 py-3 text-left">Business</th>
                        <th className="px-4 py-3 text-left">Jurisdiction</th>
                        <th className="px-4 py-3 text-left">Last Audit</th>
                        <th className="px-4 py-3 text-left">Risk Score</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayBusinessRows.length ? (
                        displayBusinessRows.map((row) => (
                          <OverviewRow
                            key={row.businessId}
                            row={row}
                            active={row.businessId === effectiveBusinessId}
                            onSelect={() => handleSelectBusiness(row.businessId)}
                          />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="px-4 py-6 text-sm text-slate-600">
                            No entities found yet. Add a business to begin.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            <PaidAssessmentAllowancePanel />

            <div ref={currentRiskPositionRef} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-400">Current Risk Position</div>
                    {activeEntityName ? (
                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                        {activeEntityName}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-2 text-sm text-slate-600">
                    Current entity position based on the latest completed audit.
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm sm:min-w-[170px]">
                    <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-500">Risk Score</div>
                    <div className="mt-1 text-3xl font-extrabold tracking-tight text-slate-900">{activeRiskScore == null ? "—" : Math.round(activeRiskScore)}</div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm sm:min-w-[170px]">
                    <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-500">Risk Grade</div>
                    <div className="mt-1 text-3xl font-extrabold tracking-tight text-slate-900">{activeRiskGrade}</div>
                    <div className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{activeRiskBandLabel}</div>
                  </div>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
                {heroCards.map((card) => (
                  <div key={card.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400">{card.label}</div>
                      {card.label === "Expected Exposure" ? (
                        <div
                          className="relative"
                          onMouseEnter={() => tooltip.setOpen(true)}
                          onMouseLeave={() => tooltip.setOpen(false)}
                          onFocus={() => tooltip.setOpen(true)}
                          onBlur={() => tooltip.setOpen(false)}
                        >
                          <button
                            type="button"
                            className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 bg-white text-[11px] font-black text-slate-500 hover:bg-slate-50"
                            aria-label="Explain expected exposure"
                          >
                            i
                          </button>
                          {tooltip.open ? (
                            <div className="absolute left-1/2 top-7 z-20 w-72 -translate-x-1/2 rounded-2xl border border-slate-200 bg-white p-3 text-xs font-medium leading-5 text-slate-600 shadow-xl">
                              Expected Exposure is calculated using enforcement weighting, governance maturity factors, violation severity, and jurisdictional precedent.
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <div className={`mt-3 text-3xl font-extrabold tracking-tight ${card.tone}`}>{card.value}</div>
                  </div>
                ))}
              </div>

              <div className="mt-5 text-sm text-slate-600">
                Risk Score reflects weighted control-gap severity. Risk Grade also respects high-gravity finding floors. Estimated Financial Exposure reflects financial consequence.
              </div>

              <div className="mt-5 flex flex-wrap gap-3 border-t border-slate-100 pt-5">
                <button
                  type="button"
                  onClick={() => navigate("/start-audit?mode=rerun")}
                  className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-black text-white hover:bg-blue-700"
                >
                  Start New Audit (for this business)
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/reports")}
                  className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-black text-blue-700 hover:bg-blue-100"
                >
                  View Reports
                </button>
                <button
                  type="button"
                  disabled={!allowRemediation}
                  onClick={() => navigate("/remediation")}
                  className={allowRemediation
                    ? "rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-black text-slate-700 hover:bg-slate-50"
                    : "cursor-not-allowed rounded-xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm font-black text-slate-400"}
                >
                  {allowRemediation ? "Go to Remediation" : "Remediation Unavailable"}
                </button>
              </div>
            </div>
          </div>

          <div className={`mt-6 rounded-3xl border p-5 shadow-sm ${exposureOutlook.tone}`}>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.25em]">Executive Exposure Signal</div>
                <div className="mt-2 flex items-center gap-3">
                  <span className={`rounded-full px-3 py-1 text-xs font-black uppercase tracking-[0.2em] ${exposureOutlook.badge}`}>
                    {exposureOutlook.label}
                  </span>
                  <span className="text-sm font-semibold">{exposureOutlook.message}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50/90 px-5 py-3 shadow-sm">
            <div className="flex flex-col items-center justify-center text-center gap-1">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                Statutory Maximum (Legal Cap)
              </div>
              <div className="text-sm text-slate-600">
                Legal ceiling shown for context. This is not the modelled exposure headline.
              </div>
              <div className="mt-1 text-lg font-medium tracking-tight text-slate-600">
                {legalCapValue}
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-12">
            <div className={topTier ? "xl:col-span-12" : "xl:col-span-8"}>
              <ExposureTrendChart points={displayedTrendPoints} currencySymbol={displayCurrencySymbol} trendSignal={trendSignal} />
            </div>

            {!topTier ? (
              <div className="xl:col-span-4">
                <div className="h-full rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="text-xs font-black uppercase tracking-[0.35em] text-slate-400">Most recent audit</div>
                  <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-500">Risk Score</div>
                    <div className="mt-2 text-4xl font-extrabold tracking-tight text-slate-900">
                      {typeof activeRiskScore === "number" ? Math.round(activeRiskScore) : "—"}
                    </div>
                    <div className="mt-2 text-sm text-slate-600">
                      {latestWhen ? (
                        <>
                          Performed: <span className="font-semibold text-slate-900">{formatWhen(latestWhen)}</span>
                        </>
                      ) : (
                        "No completed audits yet."
                      )}
                    </div>
                  </div>

                  <div className="mt-5 text-sm text-slate-600">
                    {typeof activeRiskScore === "number"
                      ? "Keep running audits to track risk exposure over time."
                      : "Run your first assessment to generate a baseline trend."}
                  </div>

                  <div className="mt-6">
                    <button
                      type="button"
                      onClick={() => navigate("/reports")}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      View Reports →
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {topTier ? (

            <div className="mt-8 rounded-3xl border border-amber-200 bg-amber-50 p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-black text-amber-950">In-progress Assessments (Multi-entity)</div>
                  <div className="mt-1 text-xs text-amber-900/80">
                    Resume any business below. Each in-progress run is saved independently.
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/start-audit?mode=new")}
                  className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  + New Business Audit →
                </button>
              </div>

              <div className="mt-4 overflow-hidden rounded-2xl border border-amber-200 bg-white">
                <div className="grid grid-cols-12 bg-amber-100/50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.25em] text-amber-900/70">
                  <div className="col-span-4">Business</div>
                  <div className="col-span-3">Progress</div>
                  <div className="col-span-3">Last Saved</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>

                {inProgressRows.length ? (
                  inProgressRows.map((r) => (
                    <div key={r.business_id} className="grid grid-cols-12 items-center border-t border-amber-100 px-4 py-3 text-sm">
                      <div className="col-span-4">
                        <div className="font-semibold text-slate-900">{r.entity_name}</div>
                        <div className="text-xs text-slate-500">
                          {Array.isArray(r.jurisdictions) && r.jurisdictions.length ? r.jurisdictions.join(", ") : "—"}
                        </div>
                      </div>

                      <div className="col-span-3 text-sm text-slate-700">
                        {r.total > 0 ? (
                          <>
                            {r.completed}/{r.total}
                            {typeof r.resumeAt === "number" ? ` · Resume at Q${r.resumeAt}` : ""}
                          </>
                        ) : (
                          <>{r.completed} answered · Resume</>
                        )}
                      </div>

                      <div className="col-span-3 text-xs text-slate-500">{r.updated_at_iso ? new Date(r.updated_at_iso).toLocaleString() : "—"}</div>

                      <div className="col-span-2 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const payload = loadBusinessPayload(r.business_id);
                            if (!payload) {
                              navigate("/start-audit?mode=rerun");
                              return;
                            }

                            setActiveBusinessId(r.business_id);
                            setConfigShim(payload);
                            navigate("/assessment");
                          }}
                          className="rounded-xl bg-slate-900 px-3 py-1.5 text-xs font-black text-white hover:bg-slate-800"
                        >
                          Resume →
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeleteInProgress(r.business_id, r.entity_name)}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-700 hover:bg-slate-50"
                          title="Deletes only in-progress answers/progress for this business"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-4 text-sm text-slate-600">No in-progress assessments found yet.</div>
                )}
              </div>
            </div>
          ) : inProgressRows.length ? (
            <div className="mt-8 rounded-3xl border border-amber-200 bg-amber-50 p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-black text-amber-950">In-progress Assessment</div>
                  <div className="mt-1 text-xs text-amber-900/80">Your progress is saved. Resume where you left off.</div>
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/start-audit?mode=rerun")}
                  className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  Continue Setup →
                </button>
              </div>

              <div className="mt-4 overflow-hidden rounded-2xl border border-amber-200 bg-white">
                <div className="grid grid-cols-12 bg-amber-100/50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.25em] text-amber-900/70">
                  <div className="col-span-6">Business</div>
                  <div className="col-span-4">Progress</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>

                {inProgressRows.map((r) => (
                  <div key={r.business_id} className="grid grid-cols-12 items-center border-t border-amber-100 px-4 py-3 text-sm">
                    <div className="col-span-6">
                      <div className="font-semibold text-slate-900">{r.entity_name}</div>
                      <div className="text-xs text-slate-500">
                        {Array.isArray(r.jurisdictions) && r.jurisdictions.length ? r.jurisdictions.join(", ") : "—"}
                      </div>
                    </div>

                    <div className="col-span-4 text-sm text-slate-700">
                      {r.total > 0 ? (
                        <>
                          {r.completed}/{r.total}
                          {typeof r.resumeAt === "number" ? ` · Resume at Q${r.resumeAt}` : ""}
                        </>
                      ) : (
                        <>{r.completed} answered · Resume</>
                      )}
                    </div>

                    <div className="col-span-2 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const payload = loadBusinessPayload(r.business_id);
                          if (payload) setConfigShim(payload);
                          setActiveBusinessId(r.business_id);
                          navigate("/assessment");
                        }}
                        className="rounded-xl bg-slate-900 px-3 py-1.5 text-xs font-black text-white hover:bg-slate-800"
                      >
                        Resume →
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDeleteInProgress(r.business_id, r.entity_name)}
                        className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-700 hover:bg-slate-50"
                        title="Deletes only in-progress answers/progress for this business"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="mt-10 pb-8 text-xs text-slate-400">© 2026 Advanced Advisory Services, LLC. All Rights Reserved.</div>
        </div>
      </main>
    </div>
  );
}
